var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';
import * as React from 'react';
import { motionPresets } from './motion/presets';
import { classNames as cn } from './utils';
const DialogContentContext = React.createContext(null);
const DialogCloseButton = ({ className }) => (_jsxs(DialogPrimitive.Close, { "data-slot": "dialog-close", className: cn('ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none max-lg:flex max-lg:size-7 max-lg:items-center max-lg:justify-center max-lg:rounded-lg [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*="size-"])]:size-4', className), children: [_jsx(XIcon, {}), _jsx("span", { className: "sr-only", children: "Close" })] }));
export function Dialog(_a) {
    var props = __rest(_a, []);
    return _jsx(DialogPrimitive.Root, Object.assign({ "data-slot": "dialog" }, props));
}
export function DialogTrigger(_a) {
    var props = __rest(_a, []);
    return _jsx(DialogPrimitive.Trigger, Object.assign({ "data-slot": "dialog-trigger" }, props));
}
export function DialogPortal(_a) {
    var props = __rest(_a, []);
    return _jsx(DialogPrimitive.Portal, Object.assign({ "data-slot": "dialog-portal" }, props));
}
export function DialogClose(_a) {
    var props = __rest(_a, []);
    return _jsx(DialogPrimitive.Close, Object.assign({ "data-slot": "dialog-close" }, props));
}
export function DialogOverlay(_a) {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (_jsx(DialogPrimitive.Overlay, Object.assign({ "data-slot": "dialog-overlay", className: cn(motionPresets.fade, 'fixed inset-0 z-50 bg-black/50', className) }, props)));
}
export function DialogContent(_a) {
    var { className, children, showCloseButton = true, onOpenAutoFocus, onCloseAutoFocus, onEscapeKeyDown } = _a, props = __rest(_a, ["className", "children", "showCloseButton", "onOpenAutoFocus", "onCloseAutoFocus", "onEscapeKeyDown"]);
    const restoreFocusRef = React.useRef(null);
    const headerIds = React.useRef(new Set());
    const [activeHeaderId, setActiveHeaderId] = React.useState(null);
    const registerHeader = React.useCallback((id) => {
        headerIds.current.add(id);
        setActiveHeaderId((current) => current !== null && current !== void 0 ? current : id);
        return () => {
            headerIds.current.delete(id);
            setActiveHeaderId((current) => {
                var _a;
                return current === id
                    ? ((_a = headerIds.current.values().next().value) !== null && _a !== void 0 ? _a : null)
                    : current;
            });
        };
    }, []);
    const context = React.useMemo(() => ({ activeHeaderId, registerHeader, showCloseButton }), [activeHeaderId, registerHeader, showCloseButton]);
    return (_jsxs(DialogPortal, { "data-slot": "dialog-portal", children: [_jsx(DialogOverlay, {}), _jsx(DialogPrimitive.Content, Object.assign({ "data-slot": "dialog-content", className: cn(motionPresets.pop, 'border-base-200 bg-card fixed top-[50%] left-[50%] z-50 grid max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto overscroll-contain rounded-2xl border p-4 shadow-xl max-lg:overflow-x-hidden! sm:max-w-lg lg:w-full', className), onOpenAutoFocus: (event) => {
                    if (!window.matchMedia('(min-width: 64rem)').matches) {
                        const activeElement = document.activeElement;
                        restoreFocusRef.current =
                            activeElement instanceof HTMLElement &&
                                activeElement !== document.body &&
                                activeElement.isConnected
                                ? activeElement
                                : null;
                    }
                    onOpenAutoFocus === null || onOpenAutoFocus === void 0 ? void 0 : onOpenAutoFocus(event);
                    if (event.defaultPrevented)
                        return;
                    // DialogHeader renders the close button before the dialog body, so
                    // Radix's default "focus the first tabbable element" lands on the X.
                    // Focus the content instead: the trap stays intact with no focus ring,
                    // and anything using `autoFocus` has already claimed focus by now.
                    event.preventDefault();
                    const content = event.currentTarget instanceof HTMLElement
                        ? event.currentTarget
                        : null;
                    if (content && !content.contains(document.activeElement)) {
                        content.focus({ preventScroll: true });
                    }
                }, onEscapeKeyDown: (event) => {
                    // Radix hears Escape before the focused field does, so a field that
                    // uses it to cancel its own edit would close the dialog with it.
                    if (event.target instanceof Element &&
                        event.target.closest('[data-cancels-on-escape]')) {
                        event.preventDefault();
                    }
                    onEscapeKeyDown === null || onEscapeKeyDown === void 0 ? void 0 : onEscapeKeyDown(event);
                }, onCloseAutoFocus: (event) => {
                    const restoreFocusTarget = restoreFocusRef.current;
                    restoreFocusRef.current = null;
                    onCloseAutoFocus === null || onCloseAutoFocus === void 0 ? void 0 : onCloseAutoFocus(event);
                    if (event.defaultPrevented)
                        return;
                    if (window.matchMedia('(min-width: 64rem)').matches)
                        return;
                    if (!(restoreFocusTarget === null || restoreFocusTarget === void 0 ? void 0 : restoreFocusTarget.isConnected))
                        return;
                    const targetStyle = window.getComputedStyle(restoreFocusTarget);
                    const isVisible = restoreFocusTarget.getClientRects().length > 0 &&
                        targetStyle.display !== 'none' &&
                        targetStyle.visibility !== 'hidden';
                    if (isVisible) {
                        restoreFocusTarget.focus({ preventScroll: true });
                        if (document.activeElement === restoreFocusTarget) {
                            event.preventDefault();
                        }
                    }
                } }, props, { children: _jsxs(DialogContentContext.Provider, { value: context, children: [children, showCloseButton && !activeHeaderId && (_jsx(DialogCloseButton, { className: "absolute top-4 right-4 z-20" }))] }) }))] }));
}
export function DialogHeader(_a) {
    var { className, children, action } = _a, props = __rest(_a, ["className", "children", "action"]);
    const context = React.useContext(DialogContentContext);
    const registerHeader = context === null || context === void 0 ? void 0 : context.registerHeader;
    const id = React.useId();
    React.useLayoutEffect(() => registerHeader === null || registerHeader === void 0 ? void 0 : registerHeader(id), [id, registerHeader]);
    return (_jsx("div", Object.assign({ "data-slot": "dialog-header", className: cn(
        // DialogContent is the scroll container and carries its own padding, so
        // a sticky child stops at the content box and leaves a 1rem strip of
        // that padding for content to scroll through. A solid shadow fills the
        // strip — a negative margin cannot, because DialogContent is a grid and
        // stretch absorbs it into the item's height instead of moving it.
        'border-base-200 bg-card sticky top-0 z-10 flex border-b pb-4 text-center shadow-[0_-1rem_0_0_var(--card)] sm:text-left', (context === null || context === void 0 ? void 0 : context.showCloseButton)
            ? 'flex-row items-start gap-2'
            : 'flex-col gap-2', className) }, props, { children: (context === null || context === void 0 ? void 0 : context.showCloseButton) ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "flex min-w-0 flex-1 flex-col gap-2", children: children }), context.activeHeaderId === id && (_jsxs("div", { className: "flex shrink-0 items-center gap-2 lg:mt-1 lg:mr-1", children: [action, action && (_jsx("span", { "aria-hidden": "true", className: "bg-base-200 h-4 w-px shrink-0" })), _jsx(DialogCloseButton, {})] }))] })) : (children) })));
}
export function DialogFooter(_a) {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (_jsx("div", Object.assign({ "data-slot": "dialog-footer", className: cn('bg-card sticky bottom-0 z-10 mt-2 flex items-stretch justify-end gap-2 pt-2 shadow-[0_1rem_0_0_var(--card)] max-lg:flex-wrap! max-lg:items-center!', className) }, props)));
}
export function DialogTitle(_a) {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (_jsx(DialogPrimitive.Title, Object.assign({ "data-slot": "dialog-title", className: cn('text-base-900 text-start text-base leading-none font-semibold', className) }, props)));
}
export function DialogDescription(_a) {
    var { className } = _a, props = __rest(_a, ["className"]);
    return (_jsx(DialogPrimitive.Description, Object.assign({ "data-slot": "dialog-description", className: cn('text-muted-foreground text-start text-sm', className) }, props)));
}
